import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, BriefcaseBusiness, FileCheck, AlertCircle, Lock, Shield, ShieldCheck, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SimpleAuthForm } from '@/components/auth/SimpleAuthForm'

interface PortalLandingProps {
  onLoginSuccess?: () => void
}

/* Staggered fade-up for children */
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.4, 0.25, 1] as const } },
}

const featurePills = [
  { icon: FileCheck, label: 'Derechos ARCO+' },
  { icon: AlertCircle, label: 'Brechas en 72h' },
  { icon: Shield, label: 'Auditoría continua' },
  { icon: Lock, label: 'Cumplimiento total' },
]

export function PortalLanding({ onLoginSuccess }: PortalLandingProps) {
  const [loginOpen, setLoginOpen] = useState(false)

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#09090b] text-[#F8F6F0] selection:bg-[#C5A84A]/30">
      {/* ── Ambient background ── */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* Gold glow top-left */}
        <div className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full bg-[#C5A84A]/15 blur-[128px]" />
        {/* Teal glow bottom-right */}
        <div className="absolute -bottom-48 -right-48 h-[450px] w-[450px] rounded-full bg-[#1ABC9C]/12 blur-[128px]" />
        {/* Purple accent top-right */}
        <div className="absolute -right-20 top-[20%] h-[300px] w-[300px] rounded-full bg-[#9B59B6]/8 blur-[100px]" />
        {/* Center gold subtle */}
        <div className="absolute left-1/2 top-1/3 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#C5A84A]/[0.04] blur-[100px]" />
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(248,246,240,0.12) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* ── Main content ── */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col px-5 py-6 sm:px-8 lg:px-10"
      >
        {/* ── Navbar ── */}
        <motion.nav variants={itemVariants} className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-transparent.png" alt="Lineage Platform" className="h-10 w-10 sm:h-11 sm:w-11 object-contain" />
            <span className="text-lg font-semibold tracking-tight text-[#C5A84A]">Lineage Platform</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 rounded-full border border-[#C5A84A]/20 bg-[#C5A84A]/5 px-4 py-1.5 text-xs font-medium text-[#F8F6F0]/60 backdrop-blur-sm">
            <ShieldCheck className="h-3.5 w-3.5 text-[#C5A84A]/60" />
            Ley 21.719 &middot; Vigencia Dic 2026
          </div>
        </motion.nav>

        {/* ── Hero section — split layout ── */}
        <div className="flex flex-1 flex-col lg:flex-row items-center justify-center gap-10 lg:gap-16 pb-10 pt-6 sm:pt-10">

          {/* ── Left: Logo grande ── */}
          <motion.div
            variants={itemVariants}
            className="flex shrink-0 items-center justify-center lg:justify-start"
          >
            <div className="relative">
              <motion.div
                className="absolute inset-0 blur-3xl"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(197,168,74,0.3) 0%, transparent 70%)',
                }}
                animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />
              <img
                src="/logo-transparent.png"
                alt="Lineage Platform"
                className="relative w-48 h-48 sm:w-56 sm:h-56 lg:w-72 lg:h-72 drop-shadow-[0_0_50px_rgba(197,168,74,0.35)] object-contain"
              />
            </div>
          </motion.div>

          {/* ── Right: Content ── */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left max-w-2xl">
            {/* Badge */}
            <motion.div variants={itemVariants} className="mb-5">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#C5A84A]/25 bg-[#C5A84A]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#C5A84A]">
                <ShieldCheck className="h-3.5 w-3.5" />
                Ley 21.719 &middot; Protección de Datos
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={itemVariants}
              className="text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl md:text-5xl"
            >
              Cumple la{' '}
              <span className="bg-gradient-to-r from-[#C5A84A] via-[#1ABC9C] to-[#3498DB] bg-clip-text text-transparent">
                Ley 21.719
              </span>{' '}
              desde el día uno
            </motion.h1>

            {/* Rainbow divider */}
            <motion.div variants={itemVariants} className="mt-4 mb-3">
              <div
                className="h-[2px] w-[180px] rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #E74C3C, #F39C12, #F1C40F, #2ECC71, #1ABC9C, #3498DB, #9B59B6)',
                  opacity: 0.5,
                }}
              />
            </motion.div>

            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="text-sm leading-relaxed text-[#F8F6F0]/60 sm:text-base"
            >
              Gestiona derechos ARCO+, notifica brechas en 72h y genera evidencia de cumplimiento
              automática para la nueva Ley de Protección de Datos Personales de Chile.
            </motion.p>

            {/* Feature pills */}
            <motion.div
              variants={itemVariants}
              className="mt-5 flex flex-wrap gap-2 sm:gap-3 justify-center lg:justify-start"
            >
              {featurePills.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-[#F8F6F0]/10 bg-[#F8F6F0]/[0.03] px-4 py-2 text-xs font-medium text-[#F8F6F0]/50 backdrop-blur-sm transition-colors hover:border-[#C5A84A]/20 hover:bg-[#C5A84A]/5 hover:text-[#C5A84A]/80"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </span>
              ))}
            </motion.div>

            {/* Trust signals */}
            <motion.div
              variants={itemVariants}
              className="mt-4 flex items-center gap-4 text-[10px] text-[#F8F6F0]/25 uppercase tracking-wider"
            >
              <span className="flex items-center gap-1.5">
                <Lock className="h-3 w-3" />
                Encriptación AES-256
              </span>
              <div className="h-3 w-px bg-[#F8F6F0]/10" />
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" />
                RBAC multinivel
              </span>
              <div className="hidden sm:block h-3 w-px bg-[#F8F6F0]/10" />
              <span className="hidden sm:flex items-center gap-1.5">
                <Shield className="h-3 w-3" />
                Auditoría completa
              </span>
            </motion.div>
          </div>
        </div>

        {/* ── Access cards ── */}
        <motion.section
          variants={itemVariants}
          className="grid w-full max-w-4xl mx-auto gap-5 sm:grid-cols-2 pb-8"
        >
          {/* Partner card */}
          <div className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-[#F8F6F0]/10 bg-[#F8F6F0]/[0.03] p-5 sm:p-7 backdrop-blur-sm transition-all duration-300 hover:border-[#1ABC9C]/25 hover:bg-[#1ABC9C]/[0.04] hover:scale-[1.01]">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#1ABC9C]/50 to-transparent" />
            <div>
              <div className="mb-4 flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1ABC9C]/10 text-[#1ABC9C] ring-1 ring-[#1ABC9C]/20">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#F8F6F0]/30">Acceso para alianzas</p>
                  <h2 className="text-xl font-semibold text-[#F8F6F0]">Soy Partner</h2>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-[#F8F6F0]/50">
                Ofrece servicios de cumplimiento Ley 21.719 a tus clientes: diagnósticos automatizados, mapas de flujo y reportes certificables.
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-[#F8F6F0]/45">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1ABC9C]/60" />
                  Plantillas de auditoría pre-configuradas para acelerar diagnósticos iniciales.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1ABC9C]/60" />
                  Exporta reportes PDF branded con logo de tu consultora en un clic.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1ABC9C]/60" />
                  Accesos temporales y roles coordinados con nuestro equipo de alianzas.
                </li>
              </ul>
            </div>
            <Button
              type="button"
              variant="outline"
              className="mt-6 w-full justify-center border-[#1ABC9C]/20 bg-[#1ABC9C]/5 text-[#F8F6F0]/80 hover:border-[#1ABC9C]/35 hover:bg-[#1ABC9C]/10 hover:text-[#F8F6F0]"
              onClick={() => window.open('mailto:partners@xgoldit.com', '_blank')}
            >
              Agendar demo para partners
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          {/* Employee card */}
          <div className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-[#C5A84A]/25 bg-[#C5A84A]/[0.05] p-5 sm:p-7 backdrop-blur-sm transition-all duration-300 hover:border-[#C5A84A]/35 hover:bg-[#C5A84A]/[0.08] hover:scale-[1.01]">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A84A]/60 to-transparent" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#C5A84A]/10 blur-3xl" />
            <div className="relative">
              <div className="mb-4 flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#C5A84A]/15 text-[#C5A84A] ring-1 ring-[#C5A84A]/25">
                  <BriefcaseBusiness className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#F8F6F0]/30">Acceso interno</p>
                  <h2 className="text-xl font-semibold text-[#F8F6F0]">Soy Empleado</h2>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-[#F8F6F0]/50">
                Gestiona el cumplimiento de tu empresa: procesa solicitudes ARCO+, registra brechas y genera evidencias para auditorías.
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-[#F8F6F0]/45">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C5A84A]/60" />
                  Responde solicitudes ARCO+ en plazo legal con flujos automatizados.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C5A84A]/60" />
                  Registra incidentes y activa notificaciones obligatorias en 72 horas.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C5A84A]/60" />
                  Dashboards de riesgo en tiempo real para DPO, Legal y Compliance.
                </li>
              </ul>
            </div>
            <Button
              type="button"
              className="relative mt-6 w-full justify-center bg-[#C5A84A] text-[#09090b] font-semibold shadow-lg shadow-[#C5A84A]/20 hover:bg-[#D4B85C]"
              onClick={() => setLoginOpen(true)}
            >
              Iniciar sesión
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </motion.section>

        {/* ── Footer ── */}
        <motion.footer
          variants={itemVariants}
          className="flex flex-col items-center gap-1 pb-3 text-center text-xs text-[#F8F6F0]/25"
        >
          <span>&copy; 2026 XGOLD IT &middot; Lineage Platform</span>
          <span>Solución de cumplimiento Ley 21.719 (Protección de Datos Personales, Chile)</span>
        </motion.footer>
      </motion.div>

      {/* ── Login dialog ── */}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="max-w-md gap-0 overflow-hidden border border-border bg-card p-0 text-left shadow-xl">
          <DialogHeader className="space-y-2 border-b border-border bg-muted/40 px-6 py-5 text-left">
            <DialogTitle className="text-xl text-foreground">Iniciar sesión &middot; Lineage Platform</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Utiliza tu correo corporativo y la clave asignada para acceder a la plataforma de cumplimiento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 px-6 py-6">
            <SimpleAuthForm
              onSuccess={() => {
                setLoginOpen(false)
                onLoginSuccess?.()
              }}
              submitLabel="Ingresar"
              footerSlot={(
                <p className="text-xs text-muted-foreground">
                  Una vez autenticado, puedes cambiar tu contraseña desde el menú de tu cuenta.
                </p>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
